import React, { useState, useEffect } from 'react';
import {
    getMassnahmen,
    updateMassnahme,
    deleteMassnahme,
    getRisiken
} from '../../../../../services/schutzbedarfsanalyseService';
import MassnahmenModal from './MassnahmenModal';
import './MassnahmenSection.css';

const MassnahmenSection = () => {
    const [massnahmen, setMassnahmen] = useState([]);
    const [risiken, setRisiken] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editingMassnahme, setEditingMassnahme] = useState(null);
    const [editingRisiko, setEditingRisiko] = useState(null);

    const [filterStatus, setFilterStatus] = useState('alle');
    const [filterPrioritaet, setFilterPrioritaet] = useState('alle');
    const [filterRisiko, setFilterRisiko] = useState('alle');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        const [massnahmenResult, risikenResult] = await Promise.all([
            getMassnahmen(),
            getRisiken()
        ]);

        if (massnahmenResult.success && risikenResult.success) {
            setMassnahmen(massnahmenResult.data);
            setRisiken(risikenResult.data);
        } else {
            setError(massnahmenResult.error || risikenResult.error);
        }

        setLoading(false);
    };

    const getRisikoById = (risikoId) => {
        return risiken.find(r => r.risiko_id === risikoId);
    };

    const handleEdit = (massnahme) => {
        const risiko = getRisikoById(massnahme.risiko_id);
        setEditingMassnahme(massnahme);
        setEditingRisiko(risiko);
        setShowEditModal(true);
    };

    const handleSave = async (massnahmeId, massnahmeData) => {
        const result = await updateMassnahme(massnahmeId, massnahmeData);

        if (result.success) {
            setShowEditModal(false);
            setEditingMassnahme(null);
            setEditingRisiko(null);
            loadData();
        } else {
            alert('Fehler beim Speichern: ' + result.error);
        }
    };

    const handleDelete = async (massnahmeId) => {
        if (!window.confirm('Maßnahme wirklich löschen?')) return;

        const result = await deleteMassnahme(massnahmeId);

        if (result.success) {
            loadData();
        } else {
            alert('Fehler beim Löschen: ' + result.error);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            'geplant': { label: 'Geplant', class: 'status-geplant' },
            'in_umsetzung': { label: 'In Umsetzung', class: 'status-in_umsetzung' },
            'abgeschlossen': { label: 'Abgeschlossen', class: 'status-abgeschlossen' }
        };
        const badge = badges[status] || badges['geplant'];
        return <span className={`status-badge ${badge.class}`}>{badge.label}</span>;
    };

    const getPrioritaetBadge = (prioritaet) => {
        const badges = {
            'niedrig': { label: 'Niedrig', class: 'prioritaet-niedrig' },
            'mittel': { label: 'Mittel', class: 'prioritaet-mittel' },
            'hoch': { label: 'Hoch', class: 'prioritaet-hoch' },
            'kritisch': { label: 'Kritisch', class: 'prioritaet-kritisch' }
        };
        const badge = badges[prioritaet] || badges['mittel'];
        return <span className={`prioritaet-badge ${badge.class}`}>{badge.label}</span>;
    };

    const filteredMassnahmen = massnahmen.filter(m => {
        if (filterStatus !== 'alle' && m.status !== filterStatus) return false;
        if (filterPrioritaet !== 'alle' && m.prioritaet !== filterPrioritaet) return false;
        if (filterRisiko !== 'alle' && m.risiko_id !== filterRisiko) return false;
        return true;
    });

    if (loading) {
        return (
            <div className="massnahmen-section">
                <div className="loading-state">Lade Maßnahmen...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="massnahmen-section">
                <div className="error-state">
                    <p>Fehler: {error}</p>
                    <button onClick={loadData} className="btn-retry">Erneut versuchen</button>
                </div>
            </div>
        );
    }

    return (
        <div className="massnahmen-section">
            <div className="section-header">
                <div>
                    <h2>Maßnahmen</h2>
                    <p className="content-subtitle">
                        Übersicht aller Sicherheitsmaßnahmen
                    </p>
                    <br />
                </div>
            </div>

            <div className="filter-bar">
                <div>
                    <label>Status:</label>
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                        <option value="alle">Alle</option>
                        <option value="geplant">Geplant</option>
                        <option value="in_umsetzung">In Umsetzung</option>
                        <option value="abgeschlossen">Abgeschlossen</option>
                    </select>
                </div>
                <div>
                    <label>Priorität:</label>
                    <select value={filterPrioritaet} onChange={e => setFilterPrioritaet(e.target.value)}>
                        <option value="alle">Alle</option>
                        <option value="niedrig">Niedrig</option>
                        <option value="mittel">Mittel</option>
                        <option value="hoch">Hoch</option>
                        <option value="kritisch">Kritisch</option>
                    </select>
                </div>
                <div>
                    <label>Risiko:</label>
                    <select value={filterRisiko} onChange={e => setFilterRisiko(e.target.value)}>
                        <option value="alle">Alle</option>
                        {risiken.map(r => (
                            <option key={r.risiko_id} value={r.risiko_id}>
                                {r.risiko_bezeichnung}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {filteredMassnahmen.length === 0 ? (
                <div className="empty-state">
                    <h3>Keine Maßnahmen vorhanden</h3>
                    <p>Maßnahmen können nur direkt an einem Risiko erfasst werden</p>
                </div>
            ) : (
                <div className="massnahmen-list">
                    {filteredMassnahmen.map(massnahme => {
                        const risiko = getRisikoById(massnahme.risiko_id);
                        
                        return (
                            <div key={massnahme.massnahme_id} className="massnahme-card-full">
                                <div className="massnahme-card-header">
                                    <div>
                                        <h4>{massnahme.massnahme_titel}</h4>
                                        {risiko && (
                                            <div className="risiko-reference">
                                                🎯 Risiko: <strong>{risiko.risiko_bezeichnung}</strong>
                                                <span className="risiko-score">Score: {risiko.risikoscore}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        {massnahme.prioritaet && getPrioritaetBadge(massnahme.prioritaet)}
                                        {massnahme.status && getStatusBadge(massnahme.status)}
                                    </div>
                                </div>

                                {massnahme.massnahme_beschreibung && (
                                    <p className="massnahme-description">{massnahme.massnahme_beschreibung}</p>
                                )}

                                <div className="massnahme-details">
                                    <div className="detail-grid">
                                        {massnahme.massnahme_typ && (
                                            <div className="detail-item">
                                                <span className="detail-label">Typ:</span>
                                                <span className="detail-value">{massnahme.massnahme_typ}</span>
                                            </div>
                                        )}
                                        {massnahme.verantwortlicher && (
                                            <div className="detail-item">
                                                <span className="detail-label">Verantwortlich:</span>
                                                <span className="detail-value">{massnahme.verantwortlicher}</span>
                                            </div>
                                        )}
                                        {massnahme.geplantes_datum && (
                                            <div className="detail-item">
                                                <span className="detail-label">Geplant:</span>
                                                <span className="detail-value">
                                                    {new Date(massnahme.geplantes_datum).toLocaleDateString('de-DE')}
                                                </span>
                                            </div>
                                        )}
                                        {massnahme.kosten && (
                                            <div className="detail-item">
                                                <span className="detail-label">Kosten:</span>
                                                <span className="detail-value">
                                                    {massnahme.kosten.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="massnahme-actions">
                                    <button onClick={() => handleEdit(massnahme)} className="btn-secondary btn-small">
                                        Bearbeiten
                                    </button>
                                    <button onClick={() => handleDelete(massnahme.massnahme_id)} className="btn-ghost btn-small">
                                        Löschen
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {showEditModal && editingMassnahme && editingRisiko && (
                <MassnahmenModal
                    risiko={editingRisiko}
                    massnahme={editingMassnahme}
                    onSave={handleSave}
                    onClose={() => {
                        setShowEditModal(false);
                        setEditingMassnahme(null);
                        setEditingRisiko(null);
                    }}
                />
            )}
        </div>
    );
};

export default MassnahmenSection;
